import { isBoolean, isNil, isNumber, isString, toString } from 'lodash';
import { useQueryState } from 'nuqs';
import posthog from 'posthog-js';
/* eslint-disable react-hooks/exhaustive-deps */
import { useMemo, useCallback, useEffect, useState } from 'react';
import { ControllerRenderProps } from 'react-hook-form';
import { useGetPromoCode } from 'src/api/hooks/promocode';
import { useDebounce } from 'src/hooks/use-debounce';
import { useTranslate } from 'src/locales';
import { CartItemDTO, CartShopItemDTO, PromoCode, PromoCodeType } from 'src/types';

import { useSnackbar } from '../../components/snackbar';

// In cents
const FLAT_FEE = 30;

interface UseTicketCalculationsParams {
  tickets?: CartItemDTO[];
  shopItemsList?: CartShopItemDTO[];
  getValues: (name: string) => number;
  eventId: number;
  flatFee?: number;
}

const cleanWithType = (input: unknown, removeAll = false): string => {
  // Handle different types safely
  if (isNil(input)) return ''; // null or undefined
  if (isNumber(input)) return toString(input); // numbers
  if (isBoolean(input)) return toString(input); // booleans
  if (!isString(input)) return ''; // other non-string types

  const str = toString(input);

  // Use native string methods after safe type conversion
  return removeAll ? str.trim().replace(/\s+/g, '') : str.trim().replace(/\s+/g, ' ');
};

export const toCent = (amount: number) => amount * 100;
export const fromCent = (amount: number) => amount / 100;

export function useTicketCalculations({
  tickets,
  shopItemsList,
  getValues,
  eventId,
  flatFee = FLAT_FEE,
}: UseTicketCalculationsParams) {
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const [promoCode, setPromoCode] = useState<PromoCode>();
  const { t: translate } = useTranslate('common');
  const [discountedValues, setDiscountedValues] = useState({
    discountedTotal: 0,
    discountTotal: 0,
  });
  const [currentPromo, _] = useQueryState('promo', { defaultValue: '' });
  const debouncedPromoCode = useDebounce(currentPromo, 1000);

  // Calculate fees in cents to match backend
  const serviceFees = useMemo(() => {
    if (promoCode?.type === PromoCodeType.Percentage && promoCode.value === 100) {
      return 0;
    }

    // Use pre-computed fees from each ticket - fees property already includes percentage fee
    const ticketFees =
      tickets?.reduce((acc, current) => acc + toCent(current.fees) * current.quantityInCart, 0) ||
      0;

    // Use pre-computed fees from each shop item - fees property already includes percentage fee
    const shopItemFees =
      shopItemsList?.reduce(
        (acc, current) => acc + toCent(current.fees) * current.quantityInCart,
        0
      ) || 0;

    // Combine fees and add flat fee (0.30)
    // Both ticketFees and shopItemFees are in cents, flatFee is in cents (30)
    return ticketFees + shopItemFees + flatFee;
  }, [
    tickets,
    shopItemsList,
    flatFee,
    promoCode,
    tickets?.map((ticket) => ticket.quantityInCart).join(),
    shopItemsList?.map((item) => item.quantityInCart).join(),
  ]);

  // Calculate total net in cents
  const totalNet = useMemo(() => {
    const ticketNet =
      tickets?.reduce((acc, current) => acc + toCent(current.net) * current.quantityInCart, 0) || 0;

    const shopItemNet =
      shopItemsList?.reduce(
        (acc, current) => acc + toCent(current.net) * current.quantityInCart,
        0
      ) || 0;

    return ticketNet + shopItemNet;
  }, [
    tickets,
    shopItemsList,
    tickets?.map((ticket) => ticket.quantityInCart).join(),
    shopItemsList?.map((item) => item.quantityInCart).join(),
  ]);

  // Calculate subtotal in cents
  const subtotal = useMemo(() => {
    const ticketSubtotal =
      tickets?.reduce((acc, current) => acc + toCent(current.net) * current.quantityInCart, 0) || 0;

    const shopItemSubtotal =
      shopItemsList?.reduce(
        (acc, current) => acc + toCent(current.net) * current.quantityInCart,
        0
      ) || 0;

    return ticketSubtotal + shopItemSubtotal;
  }, [
    tickets,
    shopItemsList,
    tickets?.map((ticket) => ticket.quantityInCart).join(),
    shopItemsList?.map((item) => item.quantityInCart).join(),
  ]);

  // Calculate total cost in cents
  const totalCost = useMemo(() => subtotal + serviceFees, [subtotal, serviceFees]);

  useEffect(() => {
    let discountedTotal = totalCost;
    let discountTotal = 0;

    if (promoCode) {
      const promoValue = promoCode.value;
      const promoType = promoCode.type;

      switch (promoType) {
        case PromoCodeType.FlatAmount: {
          const discountInCents = toCent(promoValue);
          discountedTotal = Math.max(0, totalCost - discountInCents);
          discountTotal = Math.min(discountInCents, totalCost);
          break;
        }
        case PromoCodeType.Percentage:
          if (promoValue === 100) {
            discountedTotal = 0;
            discountTotal = totalCost;
          } else {
            // Calculate discount based on totalNet to match backend
            const discountAmount = Math.floor((totalNet * promoValue) / 100);
            discountTotal = discountAmount;
            discountedTotal = serviceFees + totalNet - discountAmount;
          }
          break;

        default:
          console.error('Unexpected promo code type', promoCode);
      }
    }

    setDiscountedValues({
      discountedTotal: Math.max(0, discountedTotal),
      discountTotal,
    });
  }, [promoCode, subtotal, totalCost, serviceFees]);

  const {
    data: fetchedPromoCode,
    isFetching,
    isSuccess,
    isError,
    error,
  } = useGetPromoCode(
    {
      eventId: Number(eventId),
      name: cleanWithType(debouncedPromoCode),
      ticketsQuantity: tickets?.reduce((acc, current) => acc + current.quantityInCart, 0) || 0,
      ticketSettingIds:
        tickets?.filter((ticket) => ticket.quantityInCart > 0).map((ticket) => ticket.id) || [],
    },
    (!!debouncedPromoCode && tickets?.some((ticket) => ticket.quantityInCart > 0)) ?? false,
    closeSnackbar
  );

  useEffect(() => {
    if (debouncedPromoCode && !isFetching) {
      if (fetchedPromoCode && isSuccess) {
        validatePromoCode(fetchedPromoCode);
        setPromoCode(fetchedPromoCode);
      } else {
        setPromoCode(undefined);
        validatePromoCode();
      }
    } else {
      setPromoCode(undefined);
      validatePromoCode();
    }
  }, [debouncedPromoCode, isFetching, isSuccess, fetchedPromoCode]);

  const validatePromoCode = useCallback(
    (promo?: PromoCode) => {
      if (tickets?.some((t: CartItemDTO) => [79, 74, 75, 76].includes(t.id) && t.quantity > 0)) {
        enqueueSnackbar(translate('errors.invalid-promo-bundle'), { variant: 'error' });
        return;
      }

      if (isError) {
        enqueueSnackbar(error?.message, { variant: 'info' });
      } else if (promo) {
        if (!promoCode)
          enqueueSnackbar(translate('snackbar.promo-applied'), { variant: 'success' });
        posthog.capture('select_promo_code', { name: promo?.name });
      }
    },
    [enqueueSnackbar, tickets, promoCode]
  );

  const hasTicketWithQuantityMoreThanZero = useMemo(
    () => tickets?.some((ticket) => ticket.quantityInCart > 0),
    [tickets?.map((ticket) => ticket.quantityInCart).join()]
  );

  const hasShopItemWithQuantityMoreThanZero = useMemo(
    () => shopItemsList?.some((item) => item.quantityInCart > 0),
    [shopItemsList?.map((ticket) => ticket.quantityInCart).join()]
  );

  const handleShopIncrease = useCallback(
    (field: ControllerRenderProps<any, `shopItems.${number}.quantityInCart`>, index: number) => {
      const currentShopItem = getValues(`shopItems.${index}`) as unknown as CartShopItemDTO;
      const effectiveMaxPerOrder = currentShopItem.maxPerOrder || Infinity;
      const effectiveMaxPerUser = currentShopItem.maxPerUser || Infinity;

      const currentMax = Math.min(
        effectiveMaxPerOrder,
        effectiveMaxPerUser,
        currentShopItem.quantityLeft || Infinity
      );

      if (currentShopItem.quantityInCart < currentMax) {
        field.onChange(currentShopItem.quantityInCart + 1);
        posthog.capture('add_shop_to_cart', { shopItemId: currentShopItem.id, eventId });
      } else {
        enqueueSnackbar(
          translate('errors.max-limit', { currentMax, itemTitle: currentShopItem.title }),
          {
            variant: 'error',
          }
        );
      }
    },
    [getValues, enqueueSnackbar, eventId]
  );

  const handleShopDecrease = useCallback(
    (field: ControllerRenderProps<any, `shopItems.${number}.quantityInCart`>, index: number) => {
      const currentShopItem = getValues(`shopItems.${index}`) as unknown as CartShopItemDTO;

      if (currentShopItem.quantityInCart > 0) {
        field.onChange(currentShopItem.quantityInCart - 1);
        posthog.capture('remove_shop_from_cart', { eventId });
      }
    },
    [getValues, eventId]
  );

  const handleIncrease = useCallback(
    (field: ControllerRenderProps<any, `tickets.${number}.quantityInCart`>, index: number) => {
      const currentTicket = getValues(`tickets.${index}`) as unknown as CartItemDTO;

      if (!tickets || index >= tickets.length) {
        enqueueSnackbar(translate('errors.tickets-not-available'), { variant: 'error' });
        return;
      }

      const effectiveMaxPerOrder = currentTicket.maxPerOrder ?? Infinity;
      const effectiveMaxPerUser = currentTicket.maxPerUser ?? Infinity;
      const currentMax = Math.min(
        effectiveMaxPerOrder,
        effectiveMaxPerUser,
        currentTicket.quantityLeft
      );
      if (currentTicket.quantityInCart < currentMax || currentTicket?.waitingListEnabled) {
        field.onChange(currentTicket.quantityInCart + 1);
        posthog.capture('add_to_cart', { ticketSettingId: currentTicket.id, eventId });
      } else {
        enqueueSnackbar(
          translate('errors.max-ticket-limit', {
            maxCount: Math.min(currentMax),
            ticketTitle: currentTicket.title,
          }),
          { variant: 'error' }
        );
      }
    },
    [tickets, getValues, enqueueSnackbar, eventId]
  );

  const handleDecrease = useCallback(
    (field: ControllerRenderProps<any, `tickets.${number}.quantityInCart`>, index: number) => {
      const currentTicket = getValues(`tickets.${index}`) as unknown as CartItemDTO;

      if (!tickets || index >= tickets.length) {
        enqueueSnackbar(translate('error.tickets-not-available'), { variant: 'error' });
        return;
      }

      if (currentTicket.quantityInCart > 0) {
        field.onChange(currentTicket.quantityInCart - 1);
        posthog.capture('remove_from_cart', { ticketSettingId: currentTicket.id, eventId });
      }
    },
    [tickets, getValues, eventId]
  );

  return {
    serviceFees: fromCent(serviceFees),
    totalCost,
    totalNet,
    promoCode,
    promoCodeError: error ?? undefined,
    isFreeTicket:
      discountedValues.discountedTotal === 0 || discountedValues.discountedTotal === flatFee,
    discountedTotalCost: fromCent(discountedValues.discountedTotal),
    appliedDiscount: fromCent(discountedValues.discountTotal),
    hasTicketWithQuantityMoreThanZero,
    hasShopItemWithQuantityMoreThanZero,
    applyPromoCode: {
      discountedTotal: fromCent(discountedValues.discountedTotal),
      discountTotal: fromCent(discountedValues.discountTotal),
    },
    isUpdating: isFetching,
    handleIncrease,
    handleDecrease,
    handleShopIncrease,
    handleShopDecrease,
  };
}
